import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { ButtonGroup, Dropdown } from 'semantic-ui-react';
import { Button } from '~/components';
import { InvestorBidStatus, QueueActionsDropdownOptions, QueueActionsButtonText, QueueActions, DealStates } from '~/constants';
import { usePageType, usePortfolioContext, useProjectContext } from '~/hooks';
import AddEditInvestorModal from './AddEditInvestorModal';
import { ProjectInvestorQueue } from '~/types/project';
import { useMutation } from 'react-query';
import ConfirmationModal from '~/modals/ConfirmationModal';
import toast from 'react-hot-toast';
import useAddOrUpdateInvestor from '~/mutations/admin/useAddOrUpdateInvestor';
import { getInvestorProjectDataroomAccessKey } from '~/requests/dataroom';
import { unassignPortfolioFromInvestor, unassignProjectFromInvestor } from '~/api/adminInvestorInvitations';
import './styles.scss';

interface InvestorQueueActionsProps {
  investorRow: ProjectInvestorQueue;
  invitationStatus: string;
}

const InvestorQueueActions = ({ 
  investorRow,
  invitationStatus,
}: InvestorQueueActionsProps) => {
  const pageType = usePageType();
  const queryClient = useQueryClient();
  const { project, fetchProject, projectState } = useProjectContext();
  const { portfolio, fetchPortfolio, portfolioState } = usePortfolioContext();
  const [modalType, setModalType] = useState<null | 'EDIT_INVESTOR' |'REMOVE_INVESTOR' | 'RECALL_INVITATION'>(null);

  const { mutateAsync: addOrUpdateInvestor, status: addOrUpdateInvestorStatus } = useAddOrUpdateInvestor();
  const investorId = investorRow?.investor?.id as string;

  const dealState = pageType === 'project' ? projectState?.dealState : portfolioState?.earliestState;
  const isInviteDisabled = dealState < DealStates.IN_REVIEW || dealState >= DealStates.IN_DILIGENCE;

  const updateInvestor = async (investorStatus: string) => {
    try {
      await addOrUpdateInvestor({
        isEditing: true,
        data: { investorId, status: investorStatus }
      });

      if (pageType === 'project') {
        fetchProject();
      } else if (pageType === 'portfolio') {
        // Refetch the full portfolio rather than optimistically patching just
        // `projects` — the queue table also reads `portfolio.projectInvestorData`,
        // which the patch wouldn't refresh.
        await fetchPortfolio();
      }
      
      //make sure auto dataroom/doc access is updated for projects or portfolios with each investor invite
      if (investorStatus === InvestorBidStatus.INVITED) {
        const id = pageType === 'project' ? project?.id : portfolio?.id;
        await queryClient.invalidateQueries(getInvestorProjectDataroomAccessKey(pageType, id));
      }
      setModalType(null);
      toast.success('Investor updated successfully', { duration: 5000 });
    } catch (err) {
      toast.error('Conductor ran into an issue while trying to update this investor. Please refresh and try again.', { duration: 5000 });
      console.error(err);
    }
  };

  const handleClick = (status?: string, queueAction?: string) => {
    const action = status || queueAction; 
  
    switch (action) {
      case InvestorBidStatus.PENDING:
        updateInvestor(InvestorBidStatus.INVITED);
        break;
        
      case InvestorBidStatus.INVITED:
      case QueueActions.EDIT:
      case InvestorBidStatus.ACCEPTED:
        setModalType('EDIT_INVESTOR');
        break;
      
      case QueueActions.RECALL_INVITATION:
        setModalType('RECALL_INVITATION');
        break;
        
      case InvestorBidStatus.DECLINED:
        updateInvestor(InvestorBidStatus.INVITED);
        break;
  
      case QueueActions.REMOVE_FROM_QUEUE:
        setModalType('REMOVE_INVESTOR');
        break;
        
      default:
        return;
    }
  };
  
  const RemoveInvestorConfirmationModal = () => {
    
    const { mutate: removeInvestor, status: removeInvestorStatus } = useMutation(
      async () => {
        try {
          if (pageType === 'project') {
            await unassignProjectFromInvestor(project?.id, { investorId: investorRow.investor.id });
            fetchProject();
          } else if (pageType === 'portfolio') {
            await unassignPortfolioFromInvestor(portfolio?.id, { investorId: investorRow.investor.id });
            fetchPortfolio();
          }
        } catch (err) {
          toast.error('Conductor ran into an issue while trying to remove this investor. Please refresh and try again.', { duration: 5000 });
          console.error(err);
        }
        setModalType(null);
      }
    ); 
  
    return (
      <ConfirmationModal 
        contentElement={
          <p>Are you sure you would like to remove this investor from the queue?</p>
        }
        confirmText='Remove investor'
        closeModal={() => setModalType(null)}
        mutate={() => removeInvestor()}
        status={removeInvestorStatus}
      />
    );
  };

  const RecallInvitationConfirmationModal = () => {
    return (
      <ConfirmationModal 
        contentElement={
          <p>Are you sure you would like to recall this invitation?</p>
        }
        confirmText='Recall invitation'
        closeModal={() => setModalType(null)}
        mutate={() => updateInvestor(InvestorBidStatus.PENDING)}
        status={addOrUpdateInvestorStatus}
      />
    );
  };

  return (
    <>
      <div className="investor-queue-actions-container">
        <ButtonGroup primary>
          <Button
            loading={addOrUpdateInvestorStatus === 'loading'}
            disabled={invitationStatus === InvestorBidStatus.PENDING && isInviteDisabled}
            onClick={() => handleClick(invitationStatus)}>
            {QueueActionsButtonText[invitationStatus]}
          </Button>
          <Dropdown
            className='button icon'
            options={QueueActionsDropdownOptions[invitationStatus]}
            trigger={<></>}
            disabled={invitationStatus === InvestorBidStatus.PENDING && isInviteDisabled}
          >
            <Dropdown.Menu>
              {QueueActionsDropdownOptions[invitationStatus]?.map((option: any) => (
                <Dropdown.Item
                  key={option.value}
                  onClick={() => {
                    handleClick(option.value);
                  }}
                >
                  {option.text}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </ButtonGroup>
      </div>
        
      {modalType === 'REMOVE_INVESTOR' && <RemoveInvestorConfirmationModal />}
      {modalType === 'RECALL_INVITATION' && <RecallInvitationConfirmationModal />}
      {modalType === 'EDIT_INVESTOR' && 
        <AddEditInvestorModal 
          investorRow={investorRow} 
          closeModal={() => setModalType(null)}
        />
      }
    </>
  );
};

export default InvestorQueueActions;